import React from 'react';
import { Card, CardContent, Box, Typography } from '@mui/material';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { useTranslate } from '@tolgee/react';
import { AwsPdfResponse } from '../AwsGraphs';

/**
 * Props for PossibleSavingsBar component.
 */
export interface PossibleSavingsBarProps {
    data: AwsPdfResponse;
}

/**
 * Renders a bar chart showing possible savings.
 * @param {PossibleSavingsBarProps} props - The props for the component.
 */
const PossibleSavingsBar: React.FC<PossibleSavingsBarProps> = ({ data }) => {
    const { t } = useTranslate();
    const options: ApexOptions = {
        chart: {
            type: 'bar',
            fontFamily: 'inherit'
        },
        colors: ['#ef11e9', '#fad0fe', '#0003c9'],
        xaxis: {
            categories: [t('pdf_success.charts.without_frust'), t('pdf_success.charts.with_frust')]
        },
        yaxis: {
            labels: {
                formatter: function (val) {
                    return val.toFixed(0);
                }
            },
            min: 0,
            max: Math.ceil(data.total_bill / 100) * 100
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '40%'
            }
        },
        dataLabels: {
            enabled: false
        }
    };
    const series = [
        {
            name: t('pdf_success.charts.annual_cost'),
            data: [data.total_bill, data.bill_with_savings]
        }
    ];
    return (
        <Box sx={{ minWidth: 0 }}>
            <Card
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '2.5px solid #000000',
                    filter: 'drop-shadow(8px 8px 0 black)',
                    borderRadius: '.5rem'
                }}
            >
                <CardContent
                    sx={{
                        pt: 2,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 2
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: 18 }}>
                            {t('pdf_success.charts.potential_savings.title')}
                        </Typography>
                        <Typography variant="h5">{t('pdf_success.charts.potential_savings.subtitle')}</Typography>
                    </Box>
                    <Chart options={options} series={series} type="bar" height={350} />
                </CardContent>
            </Card>
        </Box>
    );
};

export default PossibleSavingsBar;
